import { HoldType } from '@api';
import {
  ActionsCell,
  ArtistAnchor,
  ChallengeStatus,
  formatDate,
  StatusBadge,
  TourAnchor,
  VenueAnchor,
} from '@common';
import { DataTableColumn } from 'mantine-datatable';
import { useTranslation } from 'react-i18next';
import { getWeekdayFromDate } from './helpers';

type UseHoldsColumnsReturnType = {
  columns: DataTableColumn<HoldType>[];
};

type UseHoldsColumnsPropsType = {
  disabledColumns?: string[];
};

const TRANS_PATH = 'HOLDS.ROWS';

export const useHoldsColumns = ({
  disabledColumns = [],
}: UseHoldsColumnsPropsType = {}): UseHoldsColumnsReturnType => {
  const { t } = useTranslation('common');

  const noDataPlaceholder = t('NO_DATA_PLACEHOLDER');

  const columns: DataTableColumn<HoldType>[] = [
    {
      accessor: 'artist',
      title: t(`${TRANS_PATH}.ARTIST`),
      render: (record) => <ArtistAnchor artist={record.artist} />,
    },
    {
      accessor: 'tour',
      title: t(`${TRANS_PATH}.TOUR`),
      render: (record) =>
        record.tour ? <TourAnchor tour={record.tour} /> : noDataPlaceholder,
    },
    {
      accessor: 'weekday',
      title: t(`${TRANS_PATH}.WEEKDAY`),
      render: (record) => getWeekdayFromDate(record.showDate),
    },
    {
      accessor: 'showDate',
      title: t(`${TRANS_PATH}.SHOW_DATE`),
      render: (record) => formatDate(record.showDate),
    },
    {
      accessor: 'venue',
      title: t(`${TRANS_PATH}.VENUE`),
      render: (record) => <VenueAnchor venue={record.venue} />,
    },
    {
      accessor: 'city',
      title: t(`${TRANS_PATH}.CITY`),
      render: (record) => record.city,
    },
    {
      accessor: 'pencilPosition',
      title: t(`${TRANS_PATH}.PENCIL_POSITION`),
      render: (record) => record.pencilPosition,
    },
    {
      accessor: 'challenge',
      title: t(`${TRANS_PATH}.CHALLENGE`),
      render: (record) =>
        record.challenge ? (
          <ChallengeStatus challenge={record.challenge} />
        ) : (
          noDataPlaceholder
        ),
    },
    {
      accessor: 'confirmationDate',
      title: t(`${TRANS_PATH}.CONFIRMATION_DATE`),
      render: (record) =>
        record.confirmationDate
          ? formatDate(record.confirmationDate)
          : noDataPlaceholder,
    },
    {
      accessor: 'internalComment',
      title: t(`${TRANS_PATH}.INTERNAL_COMMENT`),
      render: (record) => record.internalComment,
      width: 200,
    },
    {
      accessor: 'tourStatus',
      title: t(`${TRANS_PATH}.TOUR_STATUS`),
      render: (record) => <StatusBadge status={record.tourStatus} />,
    },
    {
      accessor: 'actions',
      title: t(`${TRANS_PATH}.ACTIONS`),
      titleStyle: { textAlign: 'center' },
      render: (record) =>
        record.tour ? (
          <ActionsCell.Root>
            <ActionsCell.Details
              href={`/projects/touring/${record.tour.id}/avails?focus=input-${record.venue.id}-${record.showDate}`}
            />
          </ActionsCell.Root>
        ) : null,
    },
  ];

  const filteredColumns = columns.filter(
    (column) => !disabledColumns.includes(column.accessor as string)
  );

  return { columns: filteredColumns };
};
